import { InfoOutlined, Close } from "@mui/icons-material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Collapse,
  IconButton,
  Typography,
  Tooltip,
  Box,
  Button,
  Stack, Modal,
} from "@mui/material";
import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";

const FileDropzone = ({ onFileSelect, rowsPreload, originalDataBeforeUpload }) => {
  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        onFileSelect(acceptedFiles[0]);
      }
    },
    [onFileSelect]
  );

  if (!originalDataBeforeUpload.current && rowsPreload.length > 0) {
    originalDataBeforeUpload.current = JSON.parse(JSON.stringify(rowsPreload));
  }

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    acceptedFiles,
  } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "text/plain": [".txt"],
    },
  });

  return (
    <Box
      {...getRootProps()}
      sx={{
        width: "100%",
        minHeight: 50,
        padding: "8px 16px",
        border: "2px dashed #2e7d32",
        borderRadius: 2,
        cursor: "pointer",
        bgcolor: isDragActive ? "#e8f5e9" : "transparent",
        color: isDragReject ? "red" : "inherit",
        transition: "background-color 0.2s ease",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: "4px"
      }}
    >
      <input {...getInputProps()} />
      {isDragActive ? (
        <Typography variant="body2">Solte o arquivo aqui ...</Typography>
      ) : (
        <Typography variant="body2">
          Arraste um arquivo aqui ou clique para selecionar (.csv, .xlsx, .txt)
        </Typography>
      )}
      {acceptedFiles.length > 0 && (
        <Typography color="success.main" fontWeight="bold" variant="body2">
          Arquivo selecionado: {acceptedFiles[0].name}
        </Typography>
      )}
    </Box>
  );
};

const HeaderEntryData = ({
                           title,
                           instruction,
                           optional,
                           helperFileName,
                           handleFileUpload,
                           setIsInstructionOpen,
                           isUploading = false,
                           rowsPreload,
                           originalDataBeforeUpload
                         }) => {
  const [openInstruction, setOpenInstruction] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);

  const exampleFile = `/csvs/examples/exemplo_${helperFileName}.csv`;
  const namesFile = `/csvs/attributes/${helperFileName}.csv`;

  const toggleInstruction = () => {
    setOpenInstruction((prev) => !prev);
    setOpenUpload(false);
    if (setIsInstructionOpen) setIsInstructionOpen(false);
  };

  const toggleUpload = () => {
    setOpenUpload((prev) => !prev);
    setOpenInstruction(false);
    if (setIsInstructionOpen) setIsInstructionOpen(!openUpload);
  };

  const downloadFile = async (url, filename) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const content = await response.text();
      
      const utf8Bom = '\uFEFF';
      const contentWithBom = utf8Bom + content;
      
      const blob = new Blob([contentWithBom], { type: 'text/csv;charset=utf-8' });
      
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Erro ao baixar arquivo:', error);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
    }
  };

  return (
    <div className="w-full max-w-5xl flex flex-col items-center justify-center px-4">
      <div className="flex flex-col md:flex-row items-center gap-2 ">
        <h1 className="text-2xl text-center m-0 flex items-center">
          {title ? title : "Dados"}
          {optional && (
            <span className="text-yellow-400 text-sm ml-2">(ETAPA OPCIONAL)</span>
          )}
        </h1>
        <div className="flex flex-row">
          <Tooltip title={isUploading ? "Fazendo upload..." : "Importar arquivo de dados"}>
            <IconButton
              size="small"
              onClick={toggleUpload}
              disabled={isUploading}
              sx={{
                color: isUploading ? "#ccc" : "#2e7d32",
                mt: "2px",
                "&:hover": {
                  color: isUploading ? "#ccc" : "#1b5e20",
                },
              }}
            >
              <UploadFileIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Instruções da etapa">
            <IconButton
              size="small"
              onClick={toggleInstruction}
              sx={{
                color: "#0288d1",
                mt: "2px",
                "&:hover": {
                  animation: "none",
                  color: "#01579b",
                },
              }}
            >
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>


      </div>

      <Collapse in={openUpload} className="mt-2 w-full">
        <Box
          className="bg-emerald-50 border border-emerald-300 rounded-md p-2 shadow-sm flex flex-col items-center gap-2"
          sx={{
            textAlign: "center",
            maxHeight: 180,
            overflowY: "auto",
            justifyContent: "center"
          }}
        >
          <Typography variant="body2" color="text.primary" sx={{ width: "100%" }}>
            Envie um arquivo CSV, XLSX ou TXT com os campos necessários conforme fornecido no exemplo (Código do exame,Cidade,UF,Quantidade,Fator de ajuste).
          </Typography>

          <FileDropzone onFileSelect={handleFileUpload} rowsPreload={rowsPreload} originalDataBeforeUpload={originalDataBeforeUpload} />

          <Stack direction="row" spacing={1} justifyContent="center" width="100%">
            <Button
              variant="outlined"
              color="success"
              size="small"
              onClick={() =>
                downloadFile(exampleFile, `${helperFileName}.csv`)
              }
            >
              Baixar Exemplo CSV
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() =>
                downloadFile(namesFile, `${helperFileName}.csv`)
              }
            >
              Baixar CSV Códigos e Nomes
            </Button>
          </Stack>
        </Box>
      </Collapse>

      <Modal
        open={openInstruction}
        onClose={() => setOpenInstruction(false)}
        aria-labelledby="instruction-modal-title"
        aria-describedby="instruction-modal-description"
      >
        <Box
          sx={{
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 400 },
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            maxHeight: "80vh",
            overflowY: "auto",
            position: "relative",
          }}
        >
          <IconButton
            onClick={() => setOpenInstruction(false)}
            sx={{
              position: "absolute !important",
              top: "8px !important",
              right: "8px !important",
              color: "#f44336 !important",
              backgroundColor: "transparent !important",
              "&:hover": {
                color: "#d32f2f !important",
                backgroundColor: "rgba(244, 67, 54, 0.04) !important",
              },
            }}
          >
            <Close fontSize="small" />
          </IconButton>

          <Typography id="instruction-modal-title" variant="h6" component="h2">
            Instruções da Etapa
          </Typography>

          <Typography
            id="instruction-modal-description"
            variant="body2"
            component="div"
            dangerouslySetInnerHTML={{
              __html: instruction
                ? instruction
                : "Instruções da etapa não definidas.",
            }}
          />
        </Box>
      </Modal>

    </div>
  );
};

export default HeaderEntryData;